import React, {
  PropsWithChildren,
  useState,
  ReactNode,
  useCallback,
  isValidElement,
  cloneElement,
  ReactElement,
} from 'react';
import styled from '@emotion/styled';
import { Stack } from '../stack';
import { buildCSSWithLength, CSSValueWithLength, space } from '../../styles';
import { useResize } from '../../hooks/useResize';
import { SAFE_TOP_BOTTOM_AREA } from './ModalOverlay';
import { FloatingTreeContext } from '../context';
import { ModalActionButton, ModalActionButtonType } from './ModalActionButton';
import { ModalButtonProps, ModalContentProps } from './Modal';
import { ModalHeader } from './ModalHeader';
import { semantic_colors, shapes } from '../../foundation';

type LayoutHeightType = {
  header: number;
  footer: number;
};

export interface FloatingModalContentProps {
  open: boolean;
  width?: CSSValueWithLength;
  /** @default 'bottom-right' */
  position?: 'bottom-right' | 'top-right' | 'bottom-left' | 'top-left';
  title?: ReactNode;
  /**
   * 기본적으로 open transition delay가 0.15s가 있습니다. delay를 제거하려면 false로 설정합니다.
   * @default true
   */
  hasTransitionDelay?: boolean;
  confirmText?: string;
  confirmButtonProps?: ModalButtonProps;
  cancelText?: string;
  cancelButtonProps?: ModalButtonProps;
  /**
   * autofocus 지정할 기본 버튼 요소
   * @default null
   */
  autoFocusButton?: ModalActionButtonType | null;
  /**
   * 헤더 영역 타이틀 좌측 backButton 사용 유무
   * @default false
   */
  backButton?: boolean;
  /** @default true */
  closable?: boolean;
  contentProps?: ModalContentProps;
  /**
   * 푸터 영역을 커스텀할 수 있습니다.
   *  - 기본 Footer(confirmText, cancelText)는 미노출됩니다.
   */
  footer?: ReactNode;
  onConfirm?: () => void;
  /**
   * reason을 포함한 cancel 이벤트를 전달합니다.
   *  - 'cancelClick' | 'escapeKeyPress' | 'overlayClick'
   */
  onCancel?: () => void;
  /**
   * Modal Header backButton 클릭 이벤트
   */
  onBack?: () => void;
}

export const FloatingModalContent = FloatingTreeContext(
  ({
    position = 'bottom-right',
    open: openProp,
    hasTransitionDelay = true,
    children,
    title,
    width = 480,
    confirmText,
    cancelText,
    closable = true,
    backButton = false,
    autoFocusButton = null,
    footer,
    confirmButtonProps,
    cancelButtonProps,
    contentProps,
    onConfirm,
    onCancel,
    onBack,
  }: PropsWithChildren<FloatingModalContentProps>) => {
    const { windowWidth, windowHeight } = useResize();
    const [contentOverflow, setContentOverflow] = useState<boolean>(false);
    const [layoutHeight, setLayoutHeight] = useState<LayoutHeightType>({
      header: 0,
      footer: 0,
    });

    const handleResize = (height: number, target: keyof LayoutHeightType) => {
      setLayoutHeight((prevLayoutHeight) => ({ ...prevLayoutHeight, [target]: height }));
    };

    const headerRef = useCallback(
      (node: HTMLElement | null) => {
        handleResize(node?.offsetHeight ?? 0, 'header');
      },
      [windowWidth, windowHeight, openProp],
    );

    const footerRef = useCallback(
      (node: HTMLElement | null) => {
        handleResize(node?.offsetHeight ?? 0, 'footer');
      },
      [windowWidth, windowHeight, openProp],
    );

    const contentRef = useCallback(
      (node: HTMLElement | null) => {
        const scrollHeight = node?.scrollHeight ?? 0;
        const offsetHeight = node?.offsetHeight ?? 0;
        setContentOverflow(scrollHeight > offsetHeight);
      },
      [windowWidth, windowHeight, openProp],
    );

    return (
      <ModalContainer
        direction='column'
        width={width}
        opened={openProp}
        position={position}
        hasTransitionDelay={hasTransitionDelay}
        p={24}
        gap={16}
      >
        {(title || backButton || closable) && (
          <ModalLayout ref={headerRef}>
            <ModalHeader
              title={title}
              backButton={backButton}
              closeButton={closable}
              onCancel={onCancel}
              onClickBackButton={onBack}
            />
          </ModalLayout>
        )}
        {children && (
          <FloatingModalBody
            ref={contentRef}
            layoutHeight={layoutHeight}
            contentOverflow={contentOverflow}
            {...contentProps}
          >
            {children}
          </FloatingModalBody>
        )}
        {footer
          ? isValidElement(footer) &&
            cloneElement(footer as ReactElement, {
              ref: footerRef,
            })
          : (confirmText || cancelText) && (
              <ModalLayout ref={footerRef}>
                <Stack direction='row' align='center'>
                  <ModalButtonContainer direction='row'>
                    {cancelText && (
                      <ModalActionButton
                        fill
                        autoFocus={autoFocusButton === 'cancel'}
                        onClick={() => onCancel?.()}
                        {...cancelButtonProps}
                      >
                        {cancelText}
                      </ModalActionButton>
                    )}
                    {confirmText && (
                      <ModalActionButton
                        fill
                        autoFocus={autoFocusButton === 'confirm'}
                        variant='filled'
                        color='primary'
                        onClick={onConfirm}
                        {...confirmButtonProps}
                      >
                        {confirmText}
                      </ModalActionButton>
                    )}
                  </ModalButtonContainer>
                </Stack>
              </ModalLayout>
            )}
      </ModalContainer>
    );
  },
);

export const FloatingModalBody = styled.div<
  ModalContentProps & { layoutHeight: LayoutHeightType; contentOverflow: boolean }
>`
  ${({ contentOverflow, scrollable }) => (contentOverflow || scrollable) && `overflow-y: auto`};
  ${({ layoutHeight, maxHeight }) => {
    const { header, footer } = layoutHeight;
    return maxHeight
      ? buildCSSWithLength('max-height', maxHeight)
      : `max-height: calc(100vh - ${SAFE_TOP_BOTTOM_AREA + header + footer}px)`;
  }};
  ${space}
`;

const ModalLayout = styled.div`
  width: 100%;
`;

const ModalButtonContainer = styled(Stack)`
  display: flex;
  width: 100%;
  gap: 8px;
  justify-content: flex-end;
`;

const ModalContainer = styled(Stack)<{
  opened: boolean;
  width?: CSSValueWithLength;
  hasTransitionDelay?: boolean;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
}>`
  position: relative;
  background-color: ${semantic_colors.background.surface};
  ${({ width }) => buildCSSWithLength('width', width)};
  transform: scale(${({ opened }) => (opened ? 1 : 0)});
  opacity: ${({ opened }) => (opened ? 1 : 0)};
  transition: transform 0.15s ease-in-out, opacity 0.15s ease-in-out;
  transition-delay: ${({ opened, hasTransitionDelay }) => (opened && hasTransitionDelay ? '0.15s' : '0s')};
  transform-origin: ${({ position }) => {
    switch (position) {
      case 'top-left':
        return 'top left';
      case 'top-right':
        return 'top right';
      case 'bottom-left':
        return 'bottom left';
      case 'bottom-right':
        return 'bottom right';
      default:
        return 'bottom right';
    }
  }};
  overflow: hidden;
  ${shapes.border_radius.large};
  ${shapes.shadow.high};
`;
